import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Lock, Eye, EyeSlash, Spinner, CheckCircle, ShieldCheck } from "@phosphor-icons/react";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    if (!token) {
      setError("Invalid reset link. Please request a new one.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4" data-testid="reset-success">
        <div className="w-full max-w-sm text-center bg-zinc-900/50 border border-emerald-500/20 rounded-sm p-8 space-y-4">
          <CheckCircle size={48} weight="fill" className="text-emerald-400 mx-auto" />
          <h2 className="text-xl font-bold text-zinc-50">Password Reset!</h2>
          <p className="text-sm text-zinc-400">Your password has been changed successfully.</p>
          <button
            onClick={() => navigate("/auth")}
            data-testid="go-to-login"
            className="w-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-sm font-medium py-2.5 rounded-sm hover:bg-amber-500/25 transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <ShieldCheck size={32} weight="bold" className="text-amber-400 mx-auto mb-2" />
          <h1 className="text-xl font-bold text-zinc-50 font-heading">Set New Password</h1>
          <p className="text-xs text-zinc-500 mt-1">Enter your new password below</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-zinc-900/50 border border-amber-500/20 rounded-sm p-6 space-y-4" data-testid="reset-form">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1.5">New Password</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Min 6 characters"
                required
                minLength={6}
                data-testid="reset-password"
                className="w-full bg-zinc-950 border border-zinc-800 rounded-sm pl-9 pr-10 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400">
                {showPw ? <EyeSlash size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1.5">Confirm Password</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                type={showPw ? "text" : "password"}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                placeholder="Re-enter password"
                required
                minLength={6}
                data-testid="reset-confirm"
                className="w-full bg-zinc-950 border border-zinc-800 rounded-sm pl-9 pr-3 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50"
              />
            </div>
          </div>

          {error && (
            <p className="text-xs text-red-400 bg-red-900/20 border border-red-800/30 rounded-sm px-3 py-2" data-testid="reset-error">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="reset-submit"
            className="w-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-sm font-medium py-2.5 rounded-sm hover:bg-amber-500/25 transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {loading && <Spinner size={14} className="animate-spin" />}
            Reset Password
          </button>
        </form>
      </div>
    </div>
  );
}
